import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { getDvSourceImageUrl, ensureThemeJsonArray, toCssUrlValue, buildBeltItemsFromTheme } from "../../functions/themeAssets";
import { BeltItemContent } from "./BeltItemContent";
import "../fonts/breuer-headline.css";
import "./game.css";
// import "./fullhouse.css";
import Swal from "sweetalert2";
import timeIcon from "./img/time.png";
import pointIcon from "./img/point.png";

const NAMED_THEME_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

/** Resolves theme DB colors (hex or named) for inline CSS; returns undefined if unset/invalid. */
function themeColorToCss(val) {
  if (val == null || val === "") return undefined;
  if (typeof val !== "string") return undefined;
  const v = val.trim();
  if (!v) return undefined;
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_THEME_COLORS[v.toLowerCase()];
  if (named) return named;
  return undefined;
}

/** Pixels per belt background loop; must match `to` in game.css `@keyframes beltScroll`. */
const BELT_SCROLL_LOOP_PX = 600;

/** Multiply base marquee duration (>1 = slower belt + items together). */
const BELT_WELCOME_SPEED_SCALE = 1.22;

/** Portrait mobile: belt track + stickers; clamp top so the block stays on-screen. */
const BELT_LAYOUT_RESERVE_PX = 150;

/** Fixed slots inside monitor screen (cat 2) — % of .dropped-items-bin box. */
const MONITOR_DROP_SLOTS = [
  { left: 16, top: 36 },
  { left: 30, top: 34 },
  { left: 44, top: 38 },
  { left: 58, top: 34 },
  { left: 72, top: 36 },
  { left: 22, top: 54 },
  { left: 36, top: 52 },
  { left: 50, top: 56 },
  { left: 64, top: 52 },
  { left: 78, top: 54 },
];

/** Fixed slots on whiteboard (cat 3). */
const WHITEBOARD_DROP_SLOTS = [
  { left: 18, top: 30 },
  { left: 34, top: 28 },
  { left: 50, top: 32 },
  { left: 66, top: 28 },
  { left: 82, top: 30 },
  { left: 24, top: 46 },
  { left: 40, top: 44 },
  { left: 56, top: 48 },
  { left: 72, top: 44 },
  { left: 48, top: 60 },
];

/** Dynamic grid inside target zone — used on mobile portrait where slots are too wide. */
function binDropLayoutGrid(index, category, total, layoutMode) {
  const isMonitor = category === 2;
  const mobilePortrait = layoutMode === "mobilePortrait";
  const leftMin = mobilePortrait ? (isMonitor ? 26 : 24) : 18;
  const leftMax = mobilePortrait ? (isMonitor ? 74 : 76) : 82;
  const topMin = mobilePortrait ? (isMonitor ? 38 : 34) : 30;
  const topMax = mobilePortrait ? (isMonitor ? 60 : 56) : 60;
  const cols = Math.min(total, mobilePortrait ? 3 : 4);
  const col = index % cols;
  const row = Math.floor(index / cols);
  const rows = Math.ceil(total / cols);
  const left = cols <= 1 ? 50 : leftMin + (col * (leftMax - leftMin)) / (cols - 1);
  const top = rows <= 1 ? (topMin + topMax) / 2 : topMin + (row * (topMax - topMin)) / (rows - 1);
  let scale = 1;
  if (total > 4) scale = mobilePortrait ? 0.72 : 0.88;
  else if (total > 2) scale = mobilePortrait ? 0.82 : 0.92;
  else if (mobilePortrait) scale = 0.9;
  return {
    left: `${left}%`,
    top: `${top}%`,
    transform: "translate(-50%, -50%)",
    zIndex: 5 + index,
    "--drop-scale": scale,
  };
}

/** Dropped item layout inside category 2/3 targets (monitor, whiteboard). */
function binDropLayoutStyle(index, category, total = 1, layoutMode = "desktop") {
  if (layoutMode === "mobilePortrait" || (layoutMode === "mobileLandscape" && total > 2)) {
    return binDropLayoutGrid(index, category, total, layoutMode);
  }
  const slots = category === 2 ? MONITOR_DROP_SLOTS : WHITEBOARD_DROP_SLOTS;
  const slot = slots[index % slots.length] ?? slots[0];
  const many = total > 4;
  return {
    left: `${slot.left}%`,
    top: `${slot.top}%`,
    transform: "translate(-50%, -50%)",
    zIndex: 5 + index,
    ...(many ? { "--drop-scale": "0.88" } : {}),
  };
}

/**
 * Category 1 (trash): cluster items in the narrow bin interior — stays inside the mesh artwork.
 * Positions are % of .dropped-items-paper (after padding).
 */
function paperDropLayoutStyle(index, layoutMode, total = 1) {
  const mobilePortrait = layoutMode === "mobilePortrait";
  const mobileLandscape = layoutMode === "mobileLandscape";
  const compact = mobilePortrait || mobileLandscape;
  const cols = Math.min(Math.max(total, 1), mobilePortrait ? 3 : 4);
  const row = Math.floor(index / cols);
  const col = index % cols;

  const leftMin = mobilePortrait ? 46 : compact ? 44 : 42;
  const leftMax = mobilePortrait ? 54 : compact ? 56 : 58;
  const left =
    cols <= 1 ? 50 : leftMin + (col * (leftMax - leftMin)) / (cols - 1);

  const tyBase = mobilePortrait ? 3 : compact ? 4 : 6;
  const tyRowStep = mobilePortrait ? 7 : compact ? 9 : 11;
  const tyMax = mobilePortrait ? 16 : compact ? 22 : 26;
  const ty = Math.min(tyBase + row * tyRowStep, tyMax);

  const rot = ((index % 3) - 1) * (mobilePortrait ? 2 : compact ? 2.5 : 3);
  let scale = 1;
  if (total > 3) scale = mobilePortrait ? 0.78 : 0.9;
  else if (total > 2) scale = mobilePortrait ? 0.85 : 0.95;

  return {
    left: `${left}%`,
    bottom: mobilePortrait
      ? "clamp(22px, 28%, 76px)"
      : compact
        ? "clamp(20px, 26%, 74px)"
        : "clamp(34px, 30%, 80px)",
    transform: `translate(-50%, ${ty}px) rotate(${rot}deg)`,
    zIndex: 1 + index,
    "--paper-drop-scale": scale,
  };
}

function getDropLayoutMode(isMobilePortrait, isMobileLandscape) {
  if (isMobilePortrait) return "mobilePortrait";
  if (isMobileLandscape) return "mobileLandscape";
  return "desktop";
}

/** Portrait belt block height (px): min avoids a razor-thin strip; max avoids an oversized conveyor vs the art. */
const BELT_PORTRAIT_PALETTE_MIN_PX = 82;
const BELT_PORTRAIT_PALETTE_MAX_PX = 95;

/**
 * Use visualViewport when inner/layout viewport does not match what the user sees
 * (mobile URL bar, dvh vs innerHeight), so letterboxing and belt % align with the visible frame.
 */
function getViewportSizeForLayout() {
  const iw = window.innerWidth || 0;
  const ih = window.innerHeight || 0;
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  const isPortrait =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(orientation: portrait)").matches;
  const isLandscape =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(orientation: landscape)").matches;
  /** Short edge — phones in landscape stay ≤ ~480; avoids treating desktop/large tablet as “phone”. */
  const compactLandscapePhone = isLandscape && Math.min(iw, ih) <= 560;

  if (vv && vv.width > 0 && vv.height > 0) {
    if (isPortrait && iw <= 768) {
      return { vw: vv.width, vh: vv.height };
    }
    if (compactLandscapePhone) {
      return { vw: vv.width, vh: vv.height };
    }
  }
  return { vw: iw, vh: ih };
}

function normalizePlayerName(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.name;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t && !/^null(\s+null)*$/i.test(t)) return t;
  }
  const fn = user.firstName ?? user.first_name ?? "";
  const ln = user.lastName ?? user.last_name ?? "";
  const parts = [fn, ln].filter(
    (p) =>
      p != null &&
      String(p).trim() !== "" &&
      String(p).trim().toLowerCase() !== "null"
  );
  return parts.map((p) => String(p).trim()).join(" ").trim();
}

function Welcome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus } = useSelector((state) => state.theme);

  const [points, setPoints] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [bgMetrics, setBgMetrics] = useState({ offsetX: 0, offsetY: 0, dispW: 0, dispH: 0 });
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);

  const [allItems, setAllItems] = useState([]);
  const [imagesDropped, setImagesDropped] = useState(0);
  const [droppedByCategory, setDroppedByCategory] = useState({ 1: [], 2: [], 3: [] });

  const headerSet = useRef(false);
  const pointsRef = useRef(0);
  const totalImagesCount = useRef(0);
  const pointsDeltaTimerRef = useRef(null);
  const [pointsDeltaFlash, setPointsDeltaFlash] = useState(null);

  const [anim, setAnim] = useState({});
  const [pressingBelt, setPressingBelt] = useState(null);
  const [hoverTarget, setHoverTarget] = useState(null);
  /** Last zone under the pointer during drag (state can lag one frame on pointerup). */
  const hoverTargetRef = useRef(null);
  const [targetFx, setTargetFx] = useState({});
  const [tossFlyLayer, setTossFlyLayer] = useState(null);
  /** Fixed-position clone while dragging so marquee CSS transform does not move the picked item. */
  const [beltDragFly, setBeltDragFly] = useState(null);
  const beltDragFlyRef = useRef(null);

  const reportIdRef = useRef(null);
  const stageRowIdRef = useRef(null);

  const gestureRef = useRef({ id: null, x0: 0, y0: 0, x1: 0, y1: 0, moved: false, t0: 0 });
  const rafMoveRef = useRef(null);
  const latestMoveRef = useRef(null);
  const gameContainerRef = useRef(null);
  const beltMarqueeRef = useRef(null);
  const beltTrackRef = useRef(null);
  const beltMarqueeBaselineWRef = useRef(null);
  const cat1Ref = useRef(null);
  const cat2Ref = useRef(null);
  const cat3Ref = useRef(null);

  const storedUserData = JSON.parse(
    sessionStorage.getItem("userData") || "{}"
  ) || {};

  const sid = String(storedUserData?.sessionId ?? "").toLowerCase();
  const oid = String(storedUserData?.organizationId ?? "").toLowerCase();
  /** Matches DB `user_type` / login `role` (e.g. demobypass). */
  const reportRoleForApi =
    storedUserData.user_type ||
    storedUserData.userType ||
    storedUserData.role ||
    (storedUserData.source === "DEMO" ? "demobypass" : null) ||
    storedUserData.source ||
    "ORG_USER";
  const isDemoBypass =
    String(reportRoleForApi).toLowerCase() === "demobypass" ||
    storedUserData?.source === "DEMO" ||
    (sid === "demobypass" && oid === "demobypass");

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    if (headerSet.current) return;
    dispatch(setBackButtonUrl("/rules"));
    headerSet.current = true;
  }, [dispatch]);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  const wrongCountRef = useRef(0);
  useEffect(() => {
    wrongCountRef.current = wrongCount;
  }, [wrongCount]);

  useEffect(() => {
    return () => {
      if (pointsDeltaTimerRef.current) clearTimeout(pointsDeltaTimerRef.current);
    };
  }, []);

  const flashPointsDelta = (delta) => {
    if (!delta) return;
    if (pointsDeltaTimerRef.current) clearTimeout(pointsDeltaTimerRef.current);
    setPointsDeltaFlash({ key: Date.now(), delta });
    pointsDeltaTimerRef.current = setTimeout(() => {
      setPointsDeltaFlash(null);
      pointsDeltaTimerRef.current = null;
    }, 1200);
  };

  useEffect(() => {
    const body = document.body;
    const prevBg = body.style.backgroundImage;
    const prevBgSize = body.style.backgroundSize;
    const prevBgPos = body.style.backgroundPosition;
    const prevBgRepeat = body.style.backgroundRepeat;
    const prevClasses = new Set(Array.from(body.classList));

    body.classList.add("game-active");
    body.classList.remove("common-bg", "common-bg1", "rules-bg");
    body.style.backgroundImage = "none";
    body.style.backgroundSize = "";
    body.style.backgroundPosition = "";
    body.style.backgroundRepeat = "";

    return () => {
      body.classList.remove("game-active");
      body.className = "";
      prevClasses.forEach((c) => body.classList.add(c));
      body.style.backgroundImage = prevBg;
      body.style.backgroundSize = prevBgSize;
      body.style.backgroundPosition = prevBgPos;
      body.style.backgroundRepeat = prevBgRepeat;
    };
  }, []);

  useEffect(() => {
    if (themeStatus === "failed") {
      navigate("/login", { replace: true });
    }
  }, [themeStatus, navigate]);

  useEffect(() => {
    if (!storedUserData?.sessionId || isDemoBypass) return;
    const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    if (!uid || !sid || !oid || !adminToken) return;

    const syncReportAndMaybeRedirect = async () => {
      const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      if (!backendBase) return;

      const playerName = normalizePlayerName(storedUserData);

      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: sid,
            organizationId: oid,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!data.success) return;

        if (data.report) {
          const dbTime = data.report.time;
          const normalizedTime = String(dbTime ?? "").trim();
          const isZeroTime =
            !normalizedTime ||
            normalizedTime === "00:00" ||
            normalizedTime === "0" ||
            normalizedTime === "00:00:00" ||
            normalizedTime === "0:00";

          if (!isZeroTime) {
            navigate("/thankyou", {
              replace: true,
              state: {
                points: Number(data.report.points) || 0,
                time: dbTime,
              },
            });
            return;
          }

          const stageId = data.report.id;
          if (stageId != null && stageId !== "") {
            stageRowIdRef.current = String(stageId);
          }
          const extRid = data.report.reportId;
          if (extRid != null && extRid !== "") {
            reportIdRef.current = String(extRid);
            return;
          }
          /* In-progress row but no external reportId yet — seed + link below. Never use local stages.id as reportId. */
        } else {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId: sid,
              organizationId: oid,
              email: storedUserData.email || "",
              emp_id: storedUserData.employeeId || storedUserData.emp_id || "",
              name: playerName,
              user_type: reportRoleForApi,
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            stageRowIdRef.current = String(startData.id);
          }
        }

        if (reportIdRef.current) return;

        const role = reportRoleForApi;
        const seedRes = await sendReport({
          sessionId: sid,
          organizationId: oid,
          role,
          token: storedUserData.token || "",
          points: 0,
          time: "00:00",
          gameId: storedUserData.gameId,
          name: playerName,
          userId: uid,
        });
        const newRid = extractReportIdFromResponse(seedRes);
        if (newRid != null && newRid !== "") {
          reportIdRef.current = String(newRid);
          if (stageRowIdRef.current) {
            try {
              await fetch(`${backendBase}/welcomeStageLinkReport`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                  userId: uid,
                  sessionId: sid,
                  organizationId: oid,
                  stageId: stageRowIdRef.current,
                  reportId: String(newRid),
                }),
              });
            } catch (linkErr) {
              console.error("welcomeStageLinkReport:", linkErr);
            }
          }
        }
      } catch (error) {
        console.error("Report sync error:", error);
      }
    };

    syncReportAndMaybeRedirect();
  }, [adminToken, isDemoBypass, navigate]);

  useEffect(() => {
    if (!themeData) return;

    const compute = () => {
      const body = document.body;
      const { vw, vh } = getViewportSizeForLayout();
      const shortSide = Math.min(vw, vh);
      /** Portrait + narrow width → mobile portrait art. */
      const mobilePortrait =
        window.innerWidth <= 768 &&
        window.matchMedia &&
        window.matchMedia("(orientation: portrait)").matches;
      const mobileLandscape =
        window.matchMedia &&
        window.matchMedia("(orientation: landscape)").matches &&
        shortSide <= 480;
      setIsMobilePortrait(mobilePortrait);
      setIsMobileLandscape(mobileLandscape);

      const imgW = mobilePortrait ? 414 : 1920;
      const imgH = mobilePortrait ? 896 : 1080;
      /** Same 1920×1080 (or mobile portrait) letterbox math as desktop so layout % matches feedback_layout_desktop on landscape phones. */
      const scale = Math.max(vw / imgW, vh / imgH);
      const dispW = imgW * scale;
      const dispH = imgH * scale;
      const offsetX = (vw - dispW) / 2;
      const offsetY = (vh - dispH) / 2;
      setBgMetrics({ offsetX, offsetY, dispW, dispH });

      const bgUrl = mobilePortrait
        ? getDvSourceImageUrl(themeData.mobile_view_image)
        : getDvSourceImageUrl(themeData.desktop_view_image);
      body.style.backgroundImage = bgUrl ? toCssUrlValue(bgUrl) : "none";
      body.style.backgroundSize = "cover";
      body.style.backgroundPosition = "center";
      body.style.backgroundRepeat = "no-repeat";
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", compute);
      vv.addEventListener("scroll", compute);
    }

    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("orientationchange", compute);
      if (vv) {
        vv.removeEventListener("resize", compute);
        vv.removeEventListener("scroll", compute);
      }
    };
  }, [themeData]);

  useEffect(() => {
    if (!themeData) return;

    const images = buildBeltItemsFromTheme(themeData);
    totalImagesCount.current = images.length;
    const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
    setAllItems(shuffle(images));
  }, [themeData]);

  useLayoutEffect(() => {
    beltMarqueeBaselineWRef.current = null;
  }, [themeData]);

  /** Extra tail width so one loop segment ≥ belt viewport (no duplicate stickers on screen); duration scales like before. */
  useLayoutEffect(() => {
    const el = beltMarqueeRef.current;
    const track = beltTrackRef.current;
    if (!el || !track || !allItems.length) return;

    const apply = () => {
      const tw = track.clientWidth;
      if (tw <= 0) return;

      el.style.setProperty("--belt-extra-tail", "0px");
      void el.offsetWidth;
      const seg0 = el.scrollWidth / 2;
      /* Extra px so one segment ≥ track width; keep at 0 for minimal gap between copy A’s last item and copy B’s first. */
      const extra = Math.max(0, tw - seg0 + 0);
      el.style.setProperty("--belt-extra-tail", `${extra}px`);

      const w = el.scrollWidth;
      if (w <= 0) return;
      if (beltMarqueeBaselineWRef.current == null) {
        beltMarqueeBaselineWRef.current = w;
      }
      const base = beltMarqueeBaselineWRef.current;
      const sec = 28 * (w / base) * BELT_WELCOME_SPEED_SCALE;
      el.style.setProperty("--belt-marquee-duration", `${sec}s`);

      const palette = el.closest(".downpallete");
      const itemHalfPx = w / 2;
      if (palette) {
        if (palette.classList.contains("belt-loop") && itemHalfPx > 0) {
          const beltSec = (sec * BELT_SCROLL_LOOP_PX) / itemHalfPx;
          palette.style.setProperty("--belt-scroll-duration", `${beltSec}s`);
        } else {
          palette.style.removeProperty("--belt-scroll-duration");
        }
      }
    };

    apply();
    const ro = new ResizeObserver(() => apply());
    ro.observe(el);
    ro.observe(track);
    return () => {
      ro.disconnect();
      beltMarqueeRef.current
        ?.closest(".downpallete")
        ?.style.removeProperty("--belt-scroll-duration");
    };
  }, [themeData, allItems.length, bgMetrics.dispW, bgMetrics.dispH]);

  useEffect(() => {
    if (!themeData || gameComplete) return;
    const timer = setInterval(() => setElapsedTime((p) => p + 1), 1000);
    return () => clearInterval(timer);
  }, [themeData, gameComplete]);

  const formatTime = () => {
    const min = Math.floor(elapsedTime / 60);
    const sec = elapsedTime % 60;
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const completeGame = async () => {
    if (gameComplete) return;
    setGameComplete(true);

    const currentPoints = pointsRef.current;

    try {
      if (!isDemoBypass) {
        const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
        const sid = storedUserData.sessionId;
        const oid = storedUserData.organizationId;
        const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
        const finishedTime = formatTime();

        if (backendBase && stageRowIdRef.current && uid && sid && oid && adminToken) {
          try {
            await fetch(`${backendBase}/welcomeStageComplete`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId: uid,
                sessionId: sid,
                organizationId: oid,
                stageId: stageRowIdRef.current,
                time: finishedTime,
                total_score: currentPoints,
                wrongcount: wrongCountRef.current,
                currentQues: totalImagesCount.current,
              }),
            });
          } catch (stageErr) {
            console.error("welcomeStageComplete:", stageErr);
          }
        }

        const role = reportRoleForApi;
        await sendReport({
          ...(reportIdRef.current ? { reportId: reportIdRef.current } : {}),
          sessionId: sid,
          organizationId: oid,
          role,
          token: storedUserData.token || "",
          points: currentPoints,
          time: finishedTime,
          gameId: storedUserData.gameId,
          name: normalizePlayerName(storedUserData),
          userId: uid,
        });
      }
    } catch (err) {
      console.error("Score update failed:", err);
    }

    setTimeout(() => {
      navigate("/thankyou", {
              state: { points: currentPoints, time: formatTime() },
            });
    }, 1500);

    // Swal.fire({
    //   icon: "success",
    //   title: "Game Completed!",
    //   confirmButtonText: "OK",
    //   allowOutsideClick: false,
    //   allowEscapeKey: false,
    // }).then((result) => {
    //   if (result.isConfirmed) {
    //     navigate("/thankyou", {
    //       state: { points: currentPoints, time: formatTime() },
    //     });
    //   }
    // });
  };

  const adminRightPoints = parseInt(themeData?.points, 10) || 1;
  const adminWrongPoints = parseInt(themeData?.wrong_points, 10) || 0;

  if (themeStatus === "loading" || themeStatus === "idle" || !themeData) {
    return (
      <div className="fullhouse-page">
        <div className="fullhouse-loader">
          {/* <div className="quiz-loader" /> */}
        </div>
      </div>
    );
  }

  const diceImage = getDvSourceImageUrl(themeData.dice_image);

  const viewBgDesktop = getDvSourceImageUrl(themeData.desktop_view_image);
  const viewBgMobile = getDvSourceImageUrl(themeData.mobile_view_image);
  /** Portrait phone: mobile bg. Otherwise (landscape phone / desktop): desktop bg and desktop layout %. */
  const viewBg = isMobilePortrait ? viewBgMobile : viewBgDesktop;

  const safeParseJson = (v) => {
    if (!v) return null;
    if (typeof v === "object") return v;
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  };

  const feedbackLayout =
    safeParseJson(
      isMobilePortrait ? themeData.feedback_layout_mobile : themeData.feedback_layout_desktop
    ) || {
      category1: { x: 10, y: 10, w: 20, h: 20 },
      category2: { x: 40, y: 10, w: 20, h: 20 },
      category3: { x: 70, y: 10, w: 20, h: 20 },
    };

  const beltLayout = feedbackLayout?.belt;
  const useBeltLayout =
    beltLayout &&
    typeof beltLayout.x === "number" &&
    typeof beltLayout.y === "number" &&
    typeof beltLayout.w === "number";

  const cat1Img = getDvSourceImageUrl(themeData.category1_image);
  const cat2Img = getDvSourceImageUrl(themeData.category2_image);
  const cat3Img = getDvSourceImageUrl(themeData.category3_image);

  const hudTimerIconSrc = String(themeData.hud_timer_image || "").trim()
    ? getDvSourceImageUrl(themeData.hud_timer_image)
    : timeIcon;
  const hudPointsIconSrc = String(themeData.hud_points_image || "").trim()
    ? getDvSourceImageUrl(themeData.hud_points_image)
    : pointIcon;

  const hudTitleBoxBg = themeData?.belt_color || "#c4a574";
  const gameTextColorCss = themeColorToCss(themeData?.game_text_color);

  const titleDesktopFontSizePx = (() => {
    const n = parseInt(themeData?.landing_page_title_desktop_font_size_px, 10);
    if (!Number.isFinite(n)) return null;
    return Math.min(200, Math.max(8, n));
  })();

  const toCoverPx = (cat) => {
    const xPct = Number(cat?.x ?? 0);
    const yPct = Number(cat?.y ?? 0);
    const wPct = Number(cat?.w ?? 0);
    return {
      left: `${bgMetrics.offsetX + (xPct / 100) * bgMetrics.dispW}px`,
      top: `${bgMetrics.offsetY + (yPct / 100) * bgMetrics.dispH}px`,
      width: `${(wPct / 100) * bgMetrics.dispW}px`,
    };
  };

  const mergedFeedbackTitleLayout = (catNum) => {
    const ck = `category${catNum}`;
    const tk = `title${catNum}`;
    const cat = feedbackLayout?.[ck] || { x: 10, y: 10, w: 20 };
    const base = {
      x: cat.x,
      y: Math.max(0, cat.y - 6),
      w: cat.w,
      fontPct: 2.6,
    };
    const raw = feedbackLayout?.[tk];
    return raw && typeof raw === "object" ? { ...base, ...raw } : base;
  };

  /**
   * Theme belt uses x/y/w (and optional h = height % of stage art, same as categories).
   * Portrait: match the illustrated bottom band by default (span to letterbox bottom), clamped;
   * sync .belt-track height so the conveyor is neither a sliver nor an oversized slab vs CSS defaults.
   */
  const beltLayoutEffective = useBeltLayout
    ? (() => {
        if (!isMobilePortrait) {
          return { beltPx: toCoverPx(beltLayout), portraitTrackH: null };
        }
        const yPct = Number(beltLayout?.y ?? 0);
        const rawTop = bgMetrics.offsetY + (yPct / 100) * bgMetrics.dispH;
        const vhLayout = 2 * bgMetrics.offsetY + bgMetrics.dispH;
        const maxTop = Math.max(0, vhLayout - BELT_LAYOUT_RESERVE_PX);
        const clampedTop = Math.min(rawTop, maxTop);
        const spanToBottom = Math.max(
          1,
          bgMetrics.offsetY + bgMetrics.dispH - clampedTop
        );
        const hPct =
          typeof beltLayout?.h === "number" && Number.isFinite(beltLayout.h)
            ? beltLayout.h
            : null;
        const rawH =
          hPct != null ? (hPct / 100) * bgMetrics.dispH : spanToBottom;
        const paletteH = Math.round(
          Math.min(
            BELT_PORTRAIT_PALETTE_MAX_PX,
            Math.max(
              BELT_PORTRAIT_PALETTE_MIN_PX,
              Math.min(rawH, spanToBottom)
            )
          )
        );
        const portraitTrackH = Math.round(
          Math.min(98, Math.max(72, paletteH - 10))
        );
        return {
          beltPx: {
            ...toCoverPx(beltLayout),
            top: `${clampedTop}px`,
            height: `${paletteH}px`,
            minHeight: `${paletteH}px`,
            boxSizing: "border-box",
          },
          portraitTrackH,
        };
      })()
    : { beltPx: null, portraitTrackH: null };

  const beltPx = beltLayoutEffective.beltPx;
  const beltPortraitTrackH = beltLayoutEffective.portraitTrackH;

  const getTargetCategoryFromPoint = (x, y) => {
    const r1 = cat1Ref.current?.getBoundingClientRect?.();
    const r2 = cat2Ref.current?.getBoundingClientRect?.();
    const r3 = cat3Ref.current?.getBoundingClientRect?.();
    const inside = (r) => r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    if (inside(r1)) return 1;
    if (inside(r2)) return 2;
    if (inside(r3)) return 3;
    return null;
  };

  const flashTarget = (cat, kind) => {
    if (!cat) return;
    setTargetFx((p) => ({ ...p, [cat]: kind }));
    window.setTimeout(() => {
      setTargetFx((p) => {
        const next = { ...p };
        delete next[cat];
        return next;
      });
    }, kind === "hit" ? 280 : 320);
  };

  const tossItem = (item, dx, dy, endX, endY, itemEl, beltHalf) => {
    if (gameComplete) return false;
    if (!item) return false;

    const stage = gameContainerRef.current?.getBoundingClientRect?.();
    if (!stage) return false;

    const directTarget =
      typeof endX === "number" && typeof endY === "number"
        ? getTargetCategoryFromPoint(endX, endY)
        : null;

    const mag = Math.hypot(dx, dy);
    /* Short flicks toward a zone still count; ref catches last hover if lift misses the rect by a few px */
    if (mag < 24 && !directTarget && hoverTargetRef.current == null) return false;

    const dragSnap =
      beltDragFlyRef.current?.itemId === item.id ? { ...beltDragFlyRef.current } : null;
    if (dragSnap) {
      beltDragFlyRef.current = null;
      setBeltDragFly(null);
    }

    const ux = mag > 1e-6 ? dx / mag : 0;
    const uy = mag > 1e-6 ? dy / mag : 0;
    const projectedDist = Math.max(stage.width, stage.height) * 1.15;
    const landingX = gestureRef.current.x0 + ux * projectedDist;
    const landingY = gestureRef.current.y0 + uy * projectedDist;
    const landingTarget = getTargetCategoryFromPoint(landingX, landingY);
    const hoverHint = hoverTargetRef.current;

    const isCorrect =
      directTarget === item.category ||
      landingTarget === item.category ||
      (directTarget == null && landingTarget == null && hoverHint === item.category);

    const dropTarget = isCorrect
      ? item.category
      : directTarget || landingTarget || null;

    hoverTargetRef.current = null;
    setHoverTarget(null);

    if (!isCorrect) {
      const clamp = Math.min(1, mag / 120);
      const dtx = dx * 0.62;
      const dty = dy * 0.62;
      const drot = Math.max(-14, Math.min(14, dx * 0.06)) * clamp;
      const revertAnimMs = 320;

      if (dropTarget) {
        flashTarget(dropTarget, "miss");
        setWrongCount((w) => w + 1);
        flashPointsDelta(-adminWrongPoints);
        setPoints((p) => p - adminWrongPoints);
      }

      setAnim((prev) => ({
        ...prev,
        [item.id]: {
          state: "revert",
          beltHalf,
          quickRevert: true,
          fromTx: dtx,
          fromTy: dty,
          fromRot: drot,
          fromScale: 1.04,
          tx: 0,
          ty: 0,
          mx: 0,
          my: 0,
          rot: 0,
          mrot: 0,
          scale: 1,
        },
      }));

      window.setTimeout(() => {
        setAnim((prev) => {
          const { [item.id]: _, ...rest } = prev;
          return rest;
        });
      }, revertAnimMs);

      return true;
    }

    setTossFlyLayer(null);
    flashTarget(dropTarget, "hit");
    const nextDroppedTotal = imagesDropped + 1;
    flashPointsDelta(adminRightPoints);
    setPoints((p) => p + adminRightPoints);
    setDroppedByCategory((prev) => {
      const cur = prev?.[dropTarget] || [];
      if (cur.some((x) => x.id === item.id)) return prev;
      return { ...prev, [dropTarget]: [...cur, item] };
    });
    setAllItems((prev) => prev.filter((i) => i.id !== item.id));
    setImagesDropped((d) => d + 1);

    setAnim((prev) => {
      const { [item.id]: _, ...rest } = prev;
      return rest;
    });

    if (isDemoBypass && nextDroppedTotal >= 1) {
      setTimeout(() => {
        Swal.fire({
          icon: "info",
          text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          window.location.assign("https://extramileplay.com/plans");
        });
      }, 400);
      return true;
    }

    if (nextDroppedTotal >= totalImagesCount.current) {
      setTimeout(() => completeGame(), 300);
    }
    return true;
  };

  const isBeltDragging = Object.values(anim).some((a) => a?.state === "drag");

  const onItemPointerDown = (e, item, halfIdx) => {
    if (gameComplete) return;
    if (!item) return;
    try {
      e.currentTarget?.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    e.preventDefault?.();
    setPressingBelt({ id: item.id, halfIdx });
    const rect = e.currentTarget?.getBoundingClientRect?.();
    gestureRef.current = {
      id: item.id,
      halfIdx,
      x0: e.clientX,
      y0: e.clientY,
      x1: e.clientX,
      y1: e.clientY,
      moved: false,
      t0: Date.now(),
      pid: e.pointerId,
      grabOffX: rect ? e.clientX - rect.left : 0,
      grabOffY: rect ? e.clientY - rect.top : 0,
      itemUrl: item.url,
      itemKind: item.itemKind,
      itemLabel: item.label,
      itemW: rect?.width ?? 0,
      itemH: rect?.height ?? 0,
    };
  };

  const onItemPointerMove = (e) => {
    if (!gestureRef.current.id) return;
    e.preventDefault?.();
    const dx = e.clientX - gestureRef.current.x0;
    const dy = e.clientY - gestureRef.current.y0;
    if (Math.hypot(dx, dy) > 12) gestureRef.current.moved = true;
    gestureRef.current.x1 = e.clientX;
    gestureRef.current.y1 = e.clientY;

    const ht = getTargetCategoryFromPoint(e.clientX, e.clientY);
    hoverTargetRef.current = ht ?? null;
    setHoverTarget(ht);

    const halfIdx = gestureRef.current.halfIdx;
    latestMoveRef.current = {
      id: gestureRef.current.id,
      dx,
      dy,
      halfIdx,
      clientX: e.clientX,
      clientY: e.clientY,
    };
    if (rafMoveRef.current) return;

    rafMoveRef.current = window.requestAnimationFrame(() => {
      rafMoveRef.current = null;
      const mv = latestMoveRef.current;
      if (!mv?.id) return;
      const mag = Math.hypot(mv.dx, mv.dy);
      const clamp = Math.min(1, mag / 120);
      const tx = mv.dx * 0.62;
      const ty = mv.dy * 0.62;
      const rot = Math.max(-14, Math.min(14, mv.dx * 0.06)) * clamp;
      const g = gestureRef.current;
      const flyLeft =
        typeof mv.clientX === "number" && g.grabOffX != null
          ? mv.clientX - g.grabOffX
          : 0;
      const flyTop =
        typeof mv.clientY === "number" && g.grabOffY != null
          ? mv.clientY - g.grabOffY
          : 0;
      if (mag > 12 && g.itemUrl && g.itemW > 0 && g.itemH > 0) {
        beltDragFlyRef.current = {
          itemId: mv.id,
          left: flyLeft,
          top: flyTop,
          width: g.itemW,
          height: g.itemH,
        };
        setBeltDragFly({
          itemId: mv.id,
          url: g.itemUrl,
          itemKind: g.itemKind,
          label: g.itemLabel,
          left: flyLeft,
          top: flyTop,
          width: g.itemW,
          height: g.itemH,
        });
      } else if (beltDragFlyRef.current?.itemId === mv.id) {
        beltDragFlyRef.current = null;
        setBeltDragFly(null);
      }
      setAnim((prev) => ({
        ...prev,
        [mv.id]: {
          state: "drag",
          beltHalf: mv.halfIdx,
          tx,
          ty,
          mx: tx * 0.5,
          my: ty * 0.5 - 40 * clamp,
          rot,
          mrot: rot * 0.6,
          scale: 1.04,
        },
      }));
    });
  };

  const onItemPointerUp = (e, item, halfIdx) => {
    if (!gestureRef.current.id || gestureRef.current.id !== item.id) return;
    if (gestureRef.current.halfIdx !== halfIdx) return;
    try {
      e.currentTarget?.releasePointerCapture?.(gestureRef.current.pid);
    } catch {
      /* ignore */
    }
    setPressingBelt(null);
    const endX = e.clientX;
    const endY = e.clientY;
    gestureRef.current.x1 = endX;
    gestureRef.current.y1 = endY;
    const dx = endX - gestureRef.current.x0;
    const dy = endY - gestureRef.current.y0;
    const el = e.currentTarget;

    if (!gestureRef.current.moved) {
      hoverTargetRef.current = null;
      setHoverTarget(null);
      beltDragFlyRef.current = null;
      setBeltDragFly(null);
      setAnim((prev) => {
        const { [item.id]: _, ...rest } = prev;
        return rest;
      });
      gestureRef.current = { id: null };
      return;
    }

    const tossed = tossItem(item, dx, dy, endX, endY, el, halfIdx);
    if (!tossed) {
      beltDragFlyRef.current = null;
      setBeltDragFly(null);
      setAnim((prev) => {
        const { [item.id]: _, ...rest } = prev;
        return rest;
      });
    }
    hoverTargetRef.current = null;
    setHoverTarget(null);
    gestureRef.current = { id: null };
  };

  return (
    <div
      ref={gameContainerRef}
      className={`game-container bin-toss-game-touch bin-toss-welcome${isMobilePortrait ? " bin-toss--mobile-portrait" : ""}${
        isMobileLandscape ? " bin-toss--mobile-landscape" : ""
      }${isBeltDragging ? " dragging-active" : ""}`}
    >
      {viewBg ? (
        <div style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" }} className="view-bg">
          <div
            ref={cat1Ref}
            className={`target-zone ${hoverTarget === 1 ? "is-hover" : ""} ${targetFx[1] ? `fx-${targetFx[1]}` : ""}`}
            style={{
              position: "absolute",
              ...toCoverPx(feedbackLayout?.category1 || { x: 10, y: 10, w: 20 }),
              height: "auto",
              pointerEvents: "none",
            }}
          >
            <img src={cat1Img} alt="" style={{ width: "100%", height: "auto", display: "block", position: "relative", zIndex: 0 }} />
            <div className="dropped-items dropped-items-paper" aria-hidden="true">
              {(droppedByCategory?.[1] || []).map((it, i, arr) => {
                const dropLayoutMode = getDropLayoutMode(isMobilePortrait, isMobileLandscape);
                return (
                  <div
                    key={it.id}
                    className={`paper-drop paper-drop--bin${
                      it.itemKind === "name" ? " paper-drop--tile-only" : ""
                    }`}
                    style={paperDropLayoutStyle(i, dropLayoutMode, arr.length)}
                  >
                    <BeltItemContent item={it} imgClassName="category-drop-media" />
                  </div>
                );
              })}
            </div>
          </div>
          <div
            ref={cat2Ref}
            className={`target-zone ${hoverTarget === 2 ? "is-hover" : ""} ${targetFx[2] ? `fx-${targetFx[2]}` : ""}`}
            style={{
              position: "absolute",
              ...toCoverPx(feedbackLayout?.category2 || { x: 40, y: 10, w: 20, h: 20 }),
              height: "auto",
              pointerEvents: "none",
            }}
          >
            <img src={cat2Img} alt="" style={{ width: "100%", height: "auto", display: "block" }} />
            <div className="dropped-items dropped-items-bin" aria-hidden="true">
              {(droppedByCategory?.[2] || []).map((it, i, arr) => (
                <div
                  key={it.id}
                  className="bin-drop-item bin-drop-item--monitor"
                  style={binDropLayoutStyle(
                    i,
                    2,
                    arr.length,
                    getDropLayoutMode(isMobilePortrait, isMobileLandscape)
                  )}
                >
                  <BeltItemContent item={it} imgClassName="category-drop-media" />
                </div>
              ))}
            </div>
          </div>
          <div
            ref={cat3Ref}
            className={`target-zone ${hoverTarget === 3 ? "is-hover" : ""} ${targetFx[3] ? `fx-${targetFx[3]}` : ""}`}
            style={{
              position: "absolute",
              ...toCoverPx(feedbackLayout?.category3 || { x: 70, y: 10, w: 20, h: 20 }),
              height: "auto",
              pointerEvents: "none",
            }}
          >
            <img src={cat3Img} alt="" style={{ width: "100%", height: "auto", display: "block" }} />
            <div className="dropped-items dropped-items-bin" aria-hidden="true">
              {(droppedByCategory?.[3] || []).map((it, i, arr) => (
                <div
                  key={it.id}
                  className="bin-drop-item bin-drop-item--board"
                  style={binDropLayoutStyle(
                    i,
                    3,
                    arr.length,
                    getDropLayoutMode(isMobilePortrait, isMobileLandscape)
                  )}
                >
                  <BeltItemContent item={it} imgClassName="category-drop-media" />
                </div>
              ))}
            </div>
          </div>
          {[1, 2, 3].map((n) => {
            const text = String(themeData?.[`category${n}_title`] ?? "").trim();
            if (!text) return null;
            const tb = mergedFeedbackTitleLayout(n);
            const fp = Number(tb.fontPct);
            const fontPx = Math.min(
              96,
              Math.max(8, ((Number.isFinite(fp) ? fp : 2.6) / 100) * (bgMetrics.dispH || 1))
            );
            return (
              <div
                key={`fb-title-${n}`}
                className="feedback-layout-cat-title"
                style={{
                  position: "absolute",
                  ...toCoverPx(tb),
                  fontSize: `${fontPx}px`,
                  lineHeight: 1.15,
                  zIndex: 6,
                  pointerEvents: "none",
                  ...(gameTextColorCss ? { color: gameTextColorCss } : null),
                }}
              >
                {text}
              </div>
            );
          })}
        </div>
      ) : null}

      <div
        className={`downpallete ${!diceImage ? "wooden-platform" : "belt-loop"}`}
        style={{
          backgroundColor: themeData?.belt_color || "#c4a574",
          backgroundImage: diceImage ? toCssUrlValue(diceImage) : undefined,
          ...(useBeltLayout && beltPx
            ? {
                position: "absolute",
                ...beltPx,
                right: "auto",
                bottom: "auto",
              }
            : {}),
        }}
      >
        <div
          className={`belt-track${isBeltDragging ? " belt-track--drag-open" : ""}`}
          ref={beltTrackRef}
          style={
            beltPortraitTrackH != null
              ? { height: `${beltPortraitTrackH}px` }
              : undefined
          }
        >
          <div
            id="dvSource"
            ref={beltMarqueeRef}
            className="dvSource belt-marquee"
            style={{
              /* Join between half A (last item) and half B (first item) = trailing pad + next lead (::before) */
              ["--belt-half-trailing"]: "calc(max(4px, 3cqw) + var(--belt-extra-tail, 0px))",
              ["--belt-half-lead"]: "max(4px, 3cqw)",
            }}
          >
            {[0, 1].map((halfIdx) => (
              <div
                key={halfIdx === 0 ? "belt-half-a" : "belt-half-b"}
                className={`belt-half${halfIdx === 0 ? "" : " belt-half--loop-clone"}`}
                aria-hidden={halfIdx === 1 ? true : undefined}
              >
                {allItems.map((imgObj) => {
                  const st = anim[imgObj.id]?.state;
                  const gestureHalf = anim[imgObj.id]?.beltHalf;
                  const isGestureHalf =
                    (st === "drag" || st === "toss" || st === "revert") && gestureHalf === halfIdx;
                  const isPressingHere =
                    pressingBelt?.id === imgObj.id && pressingBelt?.halfIdx === halfIdx;
                  const showAnim = isGestureHalf && (st === "drag" || st === "toss" || st === "revert");
                  /* Marquee uses two belt halves; hide the non-active copy during revert so only one sticker shows */
                  const hideInactiveMarqueeCopy =
                    st === "revert" &&
                    typeof gestureHalf === "number" &&
                    gestureHalf !== halfIdx;
                  /* Drag uses a fixed overlay — hide both marquee copies so the belt keeps scrolling underneath */
                  const hideBeltDuringDrag =
                    st === "drag" && beltDragFly?.itemId === imgObj.id;
                  /* Toss animation runs on the fixed flyer — hide both belt copies */
                  const hideBeltDuringToss = st === "toss";
                  const beltSourceHidden =
                    hideInactiveMarqueeCopy || hideBeltDuringToss || hideBeltDuringDrag;
                  return (
                    <div
                      key={halfIdx === 0 ? imgObj.id : `${imgObj.id}-loop`}
                      className={[
                        "item-box",
                        isPressingHere ? "is-pressing" : "",
                        beltSourceHidden ? "belt-toss-source-hidden" : "",
                        showAnim && anim[imgObj.id]?.state === "drag" ? "is-dragging" : "",
                        showAnim && anim[imgObj.id]?.state === "revert"
                          ? `is-reverting${anim[imgObj.id]?.quickRevert ? " is-reverting--quick" : ""}`
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      data-item-id={imgObj.id}
                      data-belt-half={halfIdx}
                      onPointerDown={(e) => onItemPointerDown(e, imgObj, halfIdx)}
                      onPointerMove={onItemPointerMove}
                      onPointerUp={(e) => onItemPointerUp(e, imgObj, halfIdx)}
                      style={{
                        ...(showAnim && anim[imgObj.id]
                          ? {
                              "--tx": `${anim[imgObj.id].tx ?? 0}px`,
                              "--ty": `${anim[imgObj.id].ty ?? 0}px`,
                              "--mx": `${anim[imgObj.id].mx ?? 0}px`,
                              "--my": `${anim[imgObj.id].my ?? 0}px`,
                              "--ex": `${anim[imgObj.id].ex ?? 0}px`,
                              "--ey": `${anim[imgObj.id].ey ?? 0}px`,
                              "--rot": `${anim[imgObj.id].rot ?? 0}deg`,
                              "--mrot": `${anim[imgObj.id].mrot ?? 0}deg`,
                              "--rot-early": anim[imgObj.id].rotEarly ?? "0deg",
                              "--mrot-early": anim[imgObj.id].mrotEarly ?? "0deg",
                              "--scale": `${anim[imgObj.id].scale ?? 1}`,
                              "--from-tx": `${anim[imgObj.id].fromTx ?? anim[imgObj.id].tx ?? 0}px`,
                              "--from-ty": `${anim[imgObj.id].fromTy ?? anim[imgObj.id].ty ?? 0}px`,
                              "--from-rot": `${anim[imgObj.id].fromRot ?? anim[imgObj.id].rot ?? 0}deg`,
                              "--from-scale": `${anim[imgObj.id].fromScale ?? anim[imgObj.id].scale ?? 1}`,
                            }
                          : null),
                        touchAction: "none",
                      }}
                    >
                      <BeltItemContent item={imgObj} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {beltDragFly && anim[beltDragFly.itemId] ? (
        <div
          className="item-box is-dragging belt-drag-flyer"
          aria-hidden="true"
          style={{
            position: "fixed",
            left: beltDragFly.left,
            top: beltDragFly.top,
            width: beltDragFly.width,
            height: beltDragFly.height,
            zIndex: 88,
            pointerEvents: "none",
            boxSizing: "border-box",
            "--tx": `${anim[beltDragFly.itemId].tx ?? 0}px`,
            "--ty": `${anim[beltDragFly.itemId].ty ?? 0}px`,
            "--mx": `${anim[beltDragFly.itemId].mx ?? 0}px`,
            "--my": `${anim[beltDragFly.itemId].my ?? 0}px`,
            "--ex": `${anim[beltDragFly.itemId].ex ?? 0}px`,
            "--ey": `${anim[beltDragFly.itemId].ey ?? 0}px`,
            "--rot": `${anim[beltDragFly.itemId].rot ?? 0}deg`,
            "--mrot": `${anim[beltDragFly.itemId].mrot ?? 0}deg`,
            "--rot-early": anim[beltDragFly.itemId].rotEarly ?? "0deg",
            "--mrot-early": anim[beltDragFly.itemId].mrotEarly ?? "0deg",
            "--scale": `${anim[beltDragFly.itemId].scale ?? 1}`,
            "--from-tx": `${anim[beltDragFly.itemId].fromTx ?? anim[beltDragFly.itemId].tx ?? 0}px`,
            "--from-ty": `${anim[beltDragFly.itemId].fromTy ?? anim[beltDragFly.itemId].ty ?? 0}px`,
            "--from-rot": `${anim[beltDragFly.itemId].fromRot ?? anim[beltDragFly.itemId].rot ?? 0}deg`,
            "--from-scale": `${anim[beltDragFly.itemId].fromScale ?? anim[beltDragFly.itemId].scale ?? 1}`,
          }}
        >
          <BeltItemContent
            item={{
              url: beltDragFly.url,
              itemKind: beltDragFly.itemKind,
              label: beltDragFly.label,
            }}
          />
        </div>
      ) : null}

      {tossFlyLayer ? (
        <div
          key={tossFlyLayer.key}
          className={`belt-toss-flyer${tossFlyLayer.quick ? " is-tossing--quick" : ""}`}
          aria-hidden="true"
          style={{
            position: "fixed",
            left: tossFlyLayer.left,
            top: tossFlyLayer.top,
            width: tossFlyLayer.width,
            height: tossFlyLayer.height,
            zIndex: 90,
            "--tx": `${tossFlyLayer.tx}px`,
            "--ty": `${tossFlyLayer.ty}px`,
            "--mx": `${tossFlyLayer.mx}px`,
            "--my": `${tossFlyLayer.my}px`,
            "--ex": `${tossFlyLayer.ex}px`,
            "--ey": `${tossFlyLayer.ey}px`,
            "--rot": `${tossFlyLayer.rot}deg`,
            "--mrot": `${tossFlyLayer.mrot}deg`,
            "--rot-early": tossFlyLayer.rotEarly,
            "--mrot-early": tossFlyLayer.mrotEarly,
            "--scale": `${tossFlyLayer.scale}`,
          }}
        >
          <BeltItemContent
            item={{
              url: tossFlyLayer.url,
              itemKind: tossFlyLayer.itemKind,
              label: tossFlyLayer.label,
            }}
            imgClassName="belt-toss-flyer__img"
          />
        </div>
      ) : null}

      <div style={{ position: "relative", zIndex: 15 }}>
        <header className="game-hud-bar game-hud-bar--welcome" aria-label="Game status">
          <div className="game-hud-time">
            <img src={hudTimerIconSrc} alt="" className="game-hud-icon" width={28} height={28} />
            <span
              id="timer"
              className="game-hud-time-value"
              style={gameTextColorCss ? { color: gameTextColorCss } : undefined}
            >
              {formatTime()}
            </span>
          </div>
          <div className="game-hud-title-box">
            <h3
              className="game-hud-title-text log-title bin-toss-instruction"
              style={{
                backgroundColor: hudTitleBoxBg,
                "--game-hud-title-box-bg": hudTitleBoxBg,
                ...(gameTextColorCss ? { color: gameTextColorCss } : null),
                ...(!isMobilePortrait && titleDesktopFontSizePx != null
                  ? { fontSize: `${titleDesktopFontSizePx}px` }
                  : null),
              }}
            >
              {themeData?.landing_page_title}
            </h3>
          </div>
          <div className="game-hud-points">
            <img src={hudPointsIconSrc} alt="" className="game-hud-icon" width={28} height={28} />
            <div className="game-hud-points-inner">
              <span
                id="score"
                className="game-hud-points-value"
                style={gameTextColorCss ? { color: gameTextColorCss } : undefined}
              >
                {points}
              </span>
              {pointsDeltaFlash ? (
                <span
                  key={pointsDeltaFlash.key}
                  className={`game-hud-points-flash game-hud-points-flash--${
                    pointsDeltaFlash.delta > 0 ? "up" : "down"
                  }`}
                  aria-live="polite"
                  style={gameTextColorCss ? { color: gameTextColorCss } : undefined}
                >
                  {pointsDeltaFlash.delta > 0
                    ? `+${pointsDeltaFlash.delta}`
                    : String(pointsDeltaFlash.delta)}
                </span>
              ) : null}
            </div>
          </div>
        </header>
      </div>
    </div>
  );
}

export default Welcome;
